import { createClient } from "@supabase/supabase-js";

// ─── SINGLE SOURCE OF TRUTH for Supabase URL/key ──────────────────────────
//
// THIS IS THE ONLY FILE THAT MAY READ import.meta.env.VITE_SUPABASE_*.
// Every other module imports `supabase`, `supabaseAnon`, or `SUPABASE_URL`
// from here. If you find yourself writing `import.meta.env.VITE_SUPABASE_URL`
// in another file, STOP — import SUPABASE_URL from this module instead.
//
// Why this matters: on May 13, 2026 a new file (pages/InvoiceView.tsx) did
// `createClient(import.meta.env.VITE_SUPABASE_URL!, ...)` with no fallback.
// A build ran without .env.local in scope; Vite substituted `undefined`;
// the entire portal white-screened with "supabaseUrl is required" because
// the bad createClient ran at module-load before React mounted. The hardcoded
// fallback below means *this* file always works even without env vars, so as
// long as everyone routes through here, that whole class of bug is contained.
//
// The URL + anon key are PUBLIC (client-side only, anon role). Safe in bundle.
// ──────────────────────────────────────────────────────────────────────────

const FALLBACK_URL = "https://lxymewxomybjdrmgchqt.supabase.co";
// New-format publishable key (sb_publishable_*) — Supabase migrated away from
// legacy JWT anon keys in 2026. Both formats are accepted by the project, but
// new format is what `.env.local` carries today, so we mirror it here so the
// fallback path is the same shape the env-var path uses.
const FALLBACK_KEY = "sb_publishable_5kKeHTXXuYJMgltGHARoWg_zcXhV397";

const url =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined) || FALLBACK_URL;
const key =
  (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) || FALLBACK_KEY;

// ─── Defensive guard ──────────────────────────────────────────────────────
// If somehow the fallbacks above get blanked or env vars resolve to whitespace,
// throw a clear, actionable error instead of letting createClient throw its
// generic "supabaseUrl is required" deep inside the library (which is hard to
// trace from a minified bundle). This runs at module-load — earliest possible
// point in the app lifecycle.
//
// Key formats accepted:
//   • New publishable key: `sb_publishable_<32 chars>`  (~46 chars total)
//   • Legacy anon JWT:     `eyJ...3 dot-separated segments...` (200+ chars)
function isValidKey(k: string | undefined): boolean {
  if (!k) return false;
  if (k.startsWith("sb_publishable_") && k.length >= 30) return true;
  if (k.startsWith("eyJ") && k.split(".").length === 3 && k.length >= 100) return true;
  return false;
}
if (!url || !/^https:\/\/[a-z0-9-]+\.supabase\.co$/.test(url)) {
  throw new Error(
    `[supabase.ts] resolved URL is invalid: ${JSON.stringify(url)}. ` +
      `Check FALLBACK_URL in lib/supabase.ts or VITE_SUPABASE_URL in .env.local.`,
  );
}
if (!isValidKey(key)) {
  throw new Error(
    `[supabase.ts] resolved anon key is invalid (length=${key?.length ?? 0}, prefix=${(key ?? "").slice(0, 20)}...). ` +
      `Expected 'sb_publishable_*' or a JWT starting with 'eyJ'. ` +
      `Check FALLBACK_KEY in lib/supabase.ts or VITE_SUPABASE_ANON_KEY in .env.local.`,
  );
}

// ─── Capture hash tokens IMMEDIATELY at module load ───────────────
// Supabase's _initialize() will clear the hash after reading it.
// If _initialize() fails to establish a session (dual-client conflict,
// timing issue, etc.), the tokens are lost. Capturing here gives our
// AuthContext a fallback to manually call setSession().
export let capturedHashTokens: {
  access_token: string;
  refresh_token: string;
} | null = null;

if (typeof window !== "undefined") {
  const hash = window.location.hash;
  if (hash && hash.includes("access_token=")) {
    const params = new URLSearchParams(hash.replace("#", ""));
    const at = params.get("access_token");
    const rt = params.get("refresh_token");
    if (at && rt) {
      capturedHashTokens = { access_token: at, refresh_token: rt };
    }
  }
}

// Export the resolved URL so other modules can construct edge-function fetch()
// calls without re-reading import.meta.env (which may be undefined if no .env
// file is present during sandbox builds).
export const SUPABASE_URL = url;

export const supabase = createClient(url, key, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: "implicit",
  },
});

/**
 * Anon-only Supabase client for homeowner pages.
 * Does NOT participate in auth sessions — avoids lock contention
 * with the staff portal's AuthProvider when both run on the same domain.
 */
export const supabaseAnon = createClient(url, key, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    // Separate storage key prevents this client from interfering with
    // the main client's session persistence.
    storageKey: "sb-anon-auth-token",
  },
});
